import { AlertTriangle } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Switch } from '~/components/ui/switch';

interface QualityScoringSectionProps {
  value: { before: boolean; after: boolean };
  onToggle: (phase: 'before' | 'after') => void;
  disabled: boolean;
  /** SKU count the warning quantifies, so the cost is concrete rather than vague. */
  skuCount: number;
  /** `data-testid` root; switches get `${testId}-before-switch` / `${testId}-after-switch`. */
  testId?: string;
}

/**
 * The two quality-scoring switches: score the original product, and score the
 * enriched one.
 *
 * Two independent switches rather than a before/after/both selector because
 * both-on is the useful case — it is what produces a lift figure — and a
 * single-choice control would make it unreachable.
 *
 * One combined warning, not one per switch: the cost is a property of the run,
 * and duplicating the same sentence twice reads as two separate charges. Its
 * wording changes when both passes are on, because that is when the scorer runs
 * over the catalog twice.
 */
export function QualityScoringSection({
  value,
  onToggle,
  disabled,
  skuCount,
  testId = 'quality-scoring-section',
}: QualityScoringSectionProps) {
  const { t } = useTranslation('common');
  const baseId = useId();
  const activeCount = Number(value.before) + Number(value.after);

  const rows = [
    { phase: 'before' as const, checked: value.before },
    { phase: 'after' as const, checked: value.after },
  ];

  return (
    <section data-testid={testId} className="space-y-3">
      <div>
        <h3 className="text-foreground text-sm font-semibold">
          {t('enrichmentRun.scoring.title')}
        </h3>
        <p className="text-muted-foreground text-xs">{t('enrichmentRun.scoring.description')}</p>
      </div>

      <div className="divide-y overflow-hidden rounded-lg border">
        {rows.map(({ phase, checked }) => {
          const labelId = `${baseId}-${phase}`;
          return (
            <div key={phase} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p id={labelId} className="text-foreground text-sm font-medium">
                  {t(`enrichmentRun.scoring.${phase}.label`)}
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  {t(`enrichmentRun.scoring.${phase}.description`)}
                </p>
              </div>
              <Switch
                aria-labelledby={labelId}
                data-testid={`${testId}-${phase}-switch`}
                checked={checked}
                disabled={disabled}
                className={disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                onCheckedChange={() => {
                  onToggle(phase);
                }}
              />
            </div>
          );
        })}
      </div>

      {activeCount > 0 && (
        <p
          data-testid={`${testId}-warning`}
          className="text-warning bg-warning-50 flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs"
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          {activeCount === 2
            ? t('enrichmentRun.scoring.warningBoth', { count: skuCount })
            : t('enrichmentRun.scoring.warningSingle', { count: skuCount })}
        </p>
      )}
    </section>
  );
}
